import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { PRESETS, photoFromRestoredResult, readCapture, endCapture } from '@/native';
import { randomId } from '@/lib/checksum';
import { useAuthStore } from '@/store/auth';
import { addMedia, type DraftMedia } from './store';

/**
 * 把被安卓打断的那次拍照接回来（CR-037）。
 *
 * 相机在前台时，系统可以回收我们这个 Activity。工人拍完照回到 App，看到的是
 * **重新加载过的 WebView**：路由回到首页，`Camera.getPhoto()` 那个 Promise
 * 连同整个 JS 环境一起没了。客户报的「填写的内容在拍照后丢失」就是这件事。
 *
 * 表单内容由草稿兜住（见 store.ts），这里补上另外两半：
 *
 * 1. **把人送回那一页**。不送回去，他会站在首页，不知道刚才填的东西还在不在。
 * 2. **把那张照片捞回来**。Capacitor 把插件结果存进了 `savedInstanceState`，
 *    重建后用 `appRestoredResult` 交出来 —— 这是唯一的路径。
 *    这个事件是**保留投递**的（retainUntilConsumed），所以晚一点注册也收得到。
 *
 * 装在 `AppRoutes` 里，整个 App 只有这一份。
 */
export function useCaptureRecovery(): void {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  /**
   * 启动时把那条记录取出来就立刻销掉。
   *
   * 留着的话，工人这次主动回到首页、明天再开 App，还会被它拽回隐蔽工程页。
   * 它只该生效一次 —— 就是被系统打断之后的这一次。
   */
  const session = useRef(readCapture());
  const restored = useRef(false);

  useEffect(() => {
    if (session.current) endCapture();
  }, []);

  // ---------------------------------------------------------------- 照片
  useEffect(() => {
    const handle = CapacitorApp.addListener('appRestoredResult', (event) => {
      const pending = session.current;
      if (!pending) return;
      if (event.pluginId !== 'Camera' || event.methodName !== 'getPhoto') return;
      if (!event.success) return;

      void (async () => {
        const photo = await photoFromRestoredResult(
          event.data,
          PRESETS[pending.preset],
          // 被打断的那张没有拍摄时刻可问，用调起相机的时刻兜底 ——
          // 两者相差不过几十秒，而隐蔽工程要拿它当 capturedAt 上报
          new Date(pending.startedAt),
        );
        if (!photo) return;

        const row: DraftMedia = {
          id: randomId(),
          draftKey: pending.owner,
          blob: photo.blob,
          mimeType: photo.mimeType,
          capturedAt: photo.capturedAt.getTime(),
          isVideo: false,
          addedAt: Date.now(),
        };
        try {
          await addMedia(row);
        } catch {
          // 存不进去就只能让工人重拍一张。文字草稿仍然在
        }
      })();
    });

    return () => {
      void handle.then((h) => h.remove());
    };
  }, []);

  // ---------------------------------------------------------------- 回到那一页
  useEffect(() => {
    const pending = session.current;
    if (!pending || restored.current) return;
    // 会话还没恢复完就跳，会被 RequireAuth 弹去登录页，再被首页重定向吃掉
    if (!user) return;

    restored.current = true;
    navigate(pending.route, { replace: true });
  }, [user, navigate]);
}
